import { useState } from 'react';

import { isAxiosError } from 'axios';

import type { ExternalLink } from '@/auth/types';
import { Button, Icon, IconButton, Input, useToast } from '@/components/ui';

interface Row extends ExternalLink {
  id: number;
}

const MAX_LINKS = 20;
const MAX_LABEL = 60;
const URL_PATTERN = /^https?:\/\/\S+$/i;
const ROW_GRID = 'minmax(0, 1fr) minmax(0, 2fr) auto';

let nextRowId = 0;
const toRow = (link: ExternalLink): Row => ({ ...link, id: ++nextRowId });

const isBlank = (row: Row) => !row.label.trim() && !row.url.trim();
const labelInvalid = (row: Row) => {
  const label = row.label.trim();
  return label.length > MAX_LABEL || (!label && !!row.url.trim());
};
const urlInvalid = (row: Row) => {
  const url = row.url.trim();
  return url ? !URL_PATTERN.test(url) : !!row.label.trim();
};

function sameLinks(a: ExternalLink[], b: ExternalLink[]) {
  return a.length === b.length && a.every((l, i) => l.label === b[i].label && l.url === b[i].url);
}

function errorDescription(err: unknown): string | undefined {
  if (isAxiosError(err) && typeof err.response?.data?.error === 'string') {
    return err.response.data.error as string;
  }
  return err instanceof Error ? err.message : undefined;
}

interface ExternalLinksEditorProps {
  links: ExternalLink[];
  onSave: (links: ExternalLink[]) => Promise<unknown>;
  saving: boolean;
}

export function ExternalLinksEditor({ links, onSave, saving }: ExternalLinksEditorProps) {
  const toast = useToast();
  const [rows, setRows] = useState<Row[]>(() => links.map(toRow));

  const filled = rows.filter((r) => !isBlank(r));
  const normalized = filled.map((r) => ({ label: r.label.trim(), url: r.url.trim() }));
  const valid = filled.length <= MAX_LINKS && filled.every((r) => !labelInvalid(r) && !urlInvalid(r));
  const dirty = !sameLinks(normalized, links);

  const update = (id: number, patch: Partial<ExternalLink>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const remove = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id));

  const move = (index: number, delta: -1 | 1) =>
    setRows((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const add = () => setRows((prev) => [...prev, toRow({ label: '', url: '' })]);

  const save = async () => {
    if (!dirty || !valid) return;
    try {
      await onSave(normalized);
      setRows(normalized.map(toRow));
      toast.show({ title: 'External links updated', severity: 'success' });
    } catch (err) {
      toast.show({
        title: 'Failed to update external links',
        description: errorDescription(err),
        severity: 'error',
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
        Shown to every member of the tenant in the sidebar&apos;s External section. Each link needs a
        label and a full http(s) address and opens in a new tab.
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>No links yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: ROW_GRID,
              gap: 8,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-3)',
            }}
          >
            <span>Label</span>
            <span>URL</span>
            <span style={{ width: 92 }} />
          </div>
          {rows.map((row, i) => (
            <div
              key={row.id}
              style={{ display: 'grid', gridTemplateColumns: ROW_GRID, gap: 8, alignItems: 'center' }}
            >
              <Input
                value={row.label}
                maxLength={MAX_LABEL}
                placeholder="CMS"
                aria-label={`Link ${i + 1} label`}
                error={labelInvalid(row)}
                onChange={(e) => update(row.id, { label: e.target.value })}
              />
              <Input
                value={row.url}
                placeholder="https://example.com"
                aria-label={`Link ${i + 1} URL`}
                error={urlInvalid(row)}
                onChange={(e) => update(row.id, { url: e.target.value })}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
              <div style={{ display: 'flex', gap: 2, width: 92 }}>
                <IconButton
                  icon="chevronUp"
                  size="sm"
                  label="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                />
                <IconButton
                  icon="chevronDown"
                  size="sm"
                  label="Move down"
                  disabled={i === rows.length - 1}
                  onClick={() => move(i, 1)}
                />
                <IconButton icon="trash" size="sm" label="Remove link" onClick={() => remove(row.id)} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <Button
          variant="ghost"
          leftIcon={<Icon name="plus" size={14} />}
          onClick={add}
          disabled={rows.length >= MAX_LINKS}
        >
          Add link
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!dirty || !valid}>
          Save links
        </Button>
      </div>
    </div>
  );
}
